import React, { useEffect, useState, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './LoginForm.css';
import { RootState } from '../../store';
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import authService from "../../services/authService";
import { decodeBase64, STATUSES } from "../../comman/status";
import Background from "../BackGround/background";
import logo from "../../assets/images/logo.svg";
import Form from 'react-bootstrap/Form';
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import MadeInLoveIndia from "../common/madeInLoveIndia";
import InputGroup from "react-bootstrap/InputGroup";
import flowBackEndServices2 from "../../services/flowBackendServices";
import { DEVICE_ID_KEY, QUERY_PARAM, TV_APPLICATION_ID } from "../../utils/config";
import { toast } from "react-toastify";
import { setOtpData, setStatus } from "../../slices/authSlice";
import SupportDesktopView from './DesktopViewSupport';
import { LoginPayload, LoginResponse, RedirectPayload } from '../../models/login';
import { Errors } from '../../models/errors';
import { getDeviceID, getExtraQueryparam, getOauthAuthorizeParams, resolveRedirectUrl } from '../common/parseQueryParam';

// Interfaces
interface AuthLoginData {
    refreshToken: string;
    ssoToken: string;
    deviceId: string;
    tradingActive: boolean;
}

interface JWTPayload {
    exp: number;
    iat: number;
    iss: string;
    sub: string;
    [key: string]: any;
}

const LoginForm: React.FC = () => {
    const [searchParams] = useSearchParams();
    const { isLoggedIn, status, tokenExp, user_id, themeData, data, user, theamDataBG } = useSelector((state: RootState) => state.authUser);
    const [formState, setFormState] = useState({ user_id: '', password: '', second_auth: '', ApplicationId: TV_APPLICATION_ID, basket_id: "" });
    const [errors, setErrors] = useState<Errors>({});
    const [isLoading, setIsLoading] = useState(false);
    const [refId, setRefId] = useState<string | null>(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const decodeJWTPayload = (token: string): JWTPayload | null => {
        try {
            const parts = token.split('.');
            if (parts.length !== 3) return null;
            const payload = parts[1];
            const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
            const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
            return JSON.parse(decoded);
        } catch {
            return null;
        }
    };

    const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);

    const handleResize = () => {
        setIsDesktop(window.innerWidth > 768);
    };


    const isRefreshTokenValid = (): boolean => {
        try {
            const data = localStorage.getItem("AUTH_LOGIN");
            if (!data) return false;
            const parsed: AuthLoginData = JSON.parse(data);
            const payload = decodeJWTPayload(parsed.refreshToken);
            if (!payload) return false;
            const now = Math.floor(Date.now() / 1000);
            if (now >= payload.exp) {
                localStorage.removeItem("AUTH_LOGIN");
                return false;
            }
            return true;
        } catch {
            localStorage.removeItem("AUTH_LOGIN");
            return false;
        }
    };

    const handleLogout = () => {
        console.log("=== LOGIN FORM LOGOUT - CLEARING ALL TOKENS ===");

        // Clear all MPIN auth sessions and tokens
        sessionStorage.removeItem("MPIN_AUTH_SESSION");
        sessionStorage.removeItem("MPIN_AUTHENTICATED");
        sessionStorage.removeItem("COMING_FROM_MPIN");
        sessionStorage.removeItem("MPIN_NAVIGATION_SUCCESS");

        // CLEAN APPROACH: Clear all authentication storage
        localStorage.removeItem("PERSIST_OBJECT");
        localStorage.removeItem("AUTH_LOGIN");
        localStorage.removeItem("useRefresh");
        localStorage.removeItem("TV_ACS_TOKEN"); // If exists

        dispatch(authService.logout());
        navigate("/", { replace: true });
    };
    const checkTokenValid = () => {
        const appId = searchParams.get('applicationId') ?? "";
        const appPrefix = appId.startsWith("at_");
        if (appId && appPrefix) {
            // A ChatGPT OAuth authorize flow must complete MPin so secondFactorSso
            // can mint the authorization code — don't fast-redirect a returning
            // user through the SSO callback (getRedirecUrl), which never mints a
            // code. Fall through to the MPin screen instead.
            if (getOauthAuthorizeParams()) return;
            if (tokenExp.ssoToken) handleLogout();
            else {
                let refresh = localStorage.getItem("useRefresh");
                if (refresh) callApiAndRedirect()
            };
        }
    };

    const callApiAndRedirect = () => {
        const payload: RedirectPayload = {
            ssoToken: String(user?.ssoToken),
            applicationId: searchParams.get('applicationId')
        };
        dispatch(setStatus(STATUSES.LOADING));
        flowBackEndServices2.getRedirecUrl(payload, String(user?.access_token))
            .then((res) => {
                if (res.status === 200 && res.data?.redirectUrl) {
                    const fullRedirectUrl = resolveRedirectUrl(res.data.redirectUrl, payload.applicationId);
                    window.open(fullRedirectUrl + getExtraQueryparam(), '_self');
                } else {
                    toast.error(`${res.data?.message || "Invalid response"}`);
                }
            })
            .catch(() => toast.error("Invalid Data"))
            .finally(() => dispatch(setStatus(STATUSES.IDLE)));
    };


    const validateStoredTokens = (): boolean => {
        console.log("=== VALIDATING STORED TOKENS IN LOGIN FORM ===");

        const persisted = localStorage.getItem("PERSIST_OBJECT");
        const useRefresh = localStorage.getItem("useRefresh");
        const authLogin = localStorage.getItem("AUTH_LOGIN");

        let hasValidTokens = false;

        // Check refresh token validity
        if (persisted && useRefresh) {
            try {
                const parsed = JSON.parse(persisted);
                const refreshExpiresAt = new Date(parsed.refresh_expires_at);
                if (!refreshExpiresAt) {
                    handleLogout();
                    return false;
                  }
                const now = new Date();
                if (refreshExpiresAt && now < refreshExpiresAt) {
                  hasValidTokens=true
                }
            } catch (error) {
                console.log("❌ Error parsing PERSIST_OBJECT - clearing");
                localStorage.removeItem("PERSIST_OBJECT");
                localStorage.removeItem("useRefresh");
            }
        }

        // Check ssoToken validity
        if (authLogin) {
            try {
                const parsed = JSON.parse(authLogin);
                const ssoToken = parsed.ssoToken;

                if (ssoToken) {
                    const payload = decodeJWTPayload(ssoToken);
                    if (payload) {
                        const now = Math.floor(Date.now() / 1000);
                        if (now < payload.exp) {
                            console.log("✅ Valid ssoToken found");
                            hasValidTokens = true;
                        } else {
                            console.log("❌ ssoToken expired - clearing AUTH_LOGIN");
                            localStorage.removeItem("AUTH_LOGIN");
                        }
                    } else {
                        console.log("❌ Invalid ssoToken format - clearing AUTH_LOGIN");
                        localStorage.removeItem("AUTH_LOGIN");
                    }
                }
            } catch (error) {
                console.log("❌ Error parsing AUTH_LOGIN - clearing");
                localStorage.removeItem("AUTH_LOGIN");
            }
        }

        console.log("Token validation result:", hasValidTokens);
        return hasValidTokens;
    };

    // Check for refresh flow availability and auto-redirect
    useEffect(() => {
        console.log("LoginForm: Checking authentication state...");
        checkTokenValid();
        // Always clear MPIN sessions when showing login form
        sessionStorage.removeItem("MPIN_AUTH_SESSION");
        sessionStorage.removeItem("MPIN_AUTHENTICATED");
        sessionStorage.removeItem("COMING_FROM_MPIN");
        sessionStorage.removeItem("MPIN_NAVIGATION_SUCCESS");

        const MfaObject = localStorage.getItem("TV_ACS_TOKEN_REFRESH")
        if (MfaObject) { 
          const persisted = JSON.parse(MfaObject); 
          if (persisted.mfaType && persisted.mfaType == "qr") { 
            handleLogout(); 
            return; 
          } 
        }

        // CRITICAL: Validate stored tokens before making decisions
        const hasValidStoredTokens = validateStoredTokens();

        console.log("Checking auth state:", {
            hasValidStoredTokens,
            isLoggedIn,
            currentPath: location.pathname
        });

        // If user has valid stored tokens, redirect to MPIN
        if (hasValidStoredTokens) {
            const appId = searchParams.get('applicationId') ?? "";
            if (appId && !appId.startsWith("dev_")) {
                console.log("✅ Valid stored tokens found - redirecting to MPIN");
                navigate("/MPin", { replace: true });
                return;
            }
            return
        }

        // If no valid tokens but user appears logged in, clean up
        if (isLoggedIn && !hasValidStoredTokens) {
            console.log("❌ User appears logged in but no valid tokens - cleaning up");
            handleLogout();
            return;
        }

        // Handle special cases for already logged in users with applicationId
        if (isLoggedIn && location.pathname !== "/MPin") {
            if (searchParams.get('applicationId')) {
                return checkTokenValid();
            }
        }

        // Pre-fill formState if user_id exists
        if (user_id !== null) {
            setFormState(prev => ({ ...prev, user_id }));
        }

    }, [theamDataBG, location.pathname, isLoggedIn, user_id, navigate]);
    useEffect(() => {
        setApplicationID();
        setBasketId();
    }, []);

    const setApplicationID = () => {
        let appId = searchParams.get('applicationId') ?? themeData?.applicationId ?? TV_APPLICATION_ID;
        localStorage.setItem(QUERY_PARAM, JSON.stringify(searchParams.toString()));
        dispatch(setOtpData({ data: { ...data, applicationId: appId }, user_id: user_id ?? "" }));

    };

    const setBasketId = () => {
        const basket = searchParams.get('basket_id');
        const referral = searchParams.get('referral_id');
        if (basket) sessionStorage.setItem("public_basket_id", basket);
        if (referral) {
            sessionStorage.setItem("referral_id", referral);
            setRefId(referral);
        }
    };

    const handleFormStateChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormState({ ...formState, [name]: name === 'password' ? value : value.toUpperCase() });
        if (errors[name]) setErrors({ ...errors, [name]: null });
    };

    const findFormErrors = (): Errors => {
        const errs: Errors = {};
        if (!formState.user_id || formState.user_id.length < 4) errs.user_id = 'User ID too short or missing';
        return errs;
    };

    const handleOnClick = async (e: any) => {
        e.preventDefault();
        const trimmed = formState.user_id;
        if (trimmed.length !== 10) {
            setErrors({ user_id: "Mobile number must be exactly 10 digits" });
            return;
          }

        const errs = findFormErrors();
        if (Object.keys(errs).length > 0) setErrors(errs);
        else await flowLoginApi();
    };

    const flowLoginApi = async () => {

        setIsLoading(true);
        getDeviceID().then(async (deviceId) => {
            const dataSend: LoginPayload = {
                mobile: formState.user_id.trim(),
                deviceId: deviceId,
                applicationId: themeData?.applicationId ?? TV_APPLICATION_ID
            };
            try {
                const res = await flowBackEndServices2.flowLogin2fo(dataSend);
                const response = res.data;
                if (res.status === 200) {
                    if (response.status !== 'success') return toast.error(response.message);
                    const payload = { data: { ...dataSend, ...response }, user_id: dataSend.mobile };
                    dispatch(setOtpData(payload));
                    if (!response.mfaType && !response.token && response.pwdNeedsChange)
                        return navigate("/FlowForgetPassword#RESET", { state: payload });
                    if (response.token && response.pwdNeedsChange)
                        return navigate("/PasswordNeedChange", { state: payload });
                    if (response.status === 'success')
                        return navigate("/loginOTPVerify", { state: payload });
                }
            } catch {
                toast.error("Invalid Data");
            } finally {
                setIsLoading(false);
            }
        }).then(() => {
            setIsLoading(false);
        })

    };

    const SendOpenAccount = () => {
        const base = 'https://accounts.rupeezy.in/authentication/login';
        window.open(refId ? `${base}?c=${refId}` : base, '_blank');
    };

    const getIconAndHeader = () => (
        themeData?.themeNeed
            ? (
                <div className="pt-5">
                    <img width={'60'} height={'60'} src={'https://static.rupeezy.in/branding/blue-for-light-background.svg'} alt='Rupeezy' />
                    <div className="heading1 pt-2">Rupeezy</div>
                    <div className="heading3 colorGrey py-2">Login to {themeData.display_name} with Rupeezy</div>
                </div>
            )
            : (
                <div className="pt-3">
                    <div className="brand_lockup">
                        <img width={'34'} height={'34'} src={logo} alt='Rupeezy Web' />
                        <span className="brand_lockup_name">RUPEEZY WEB</span>
                    </div>
                    <h3 className="heading1 py-2 pt-3">Trade directly from charts</h3>
                </div>
            )
    );

    const getResponsiveLoginForm = () => (
        <div className="h-100 p-5 p-xl-5 d-flex flex-column justify-content-end">
            {themeData && getIconAndHeader()}
            <div className="align-self-start flex-fill w-100 d-flex flex-column-reverse">
                <Form className="w-100" onSubmit={handleOnClick}>
                    <Form.Group className="mb-2">
                        <Form.Label className="text1">Mobile Number</Form.Label>
                        <Form.Control
                            type="text"
                            name="user_id"
                            required
                            isInvalid={!!errors.user_id}
                            value={formState.user_id}
                            onChange={handleFormStateChange}
                            placeholder="Mobile Number"
                        />
                    </Form.Group>
                    <Button disabled={isLoading} className="blue_btn w-100 my-3" type="submit">
                        {isLoading && <Spinner as="span" animation="border" size="sm" />}
                        Login
                    </Button>
                </Form>
            </div>
            {!themeData?.permission_needed && isDesktop && (
                <div className='qr_log_switch_login py-2' onClick={() => navigate("/qrLogin?applicationId=" + getApplicationId())}>
                    login using QR Code
                </div>
            )}
            <div className="py-2">
                Don't have an account? <span onClick={SendOpenAccount} className="colorBlue c-pointer">Open an account</span>
            </div>
            <MadeInLoveIndia />
        </div>
    );

    const getApplicationId = () => {
        return themeData?.applicationId || searchParams.get("applicationId") || TV_APPLICATION_ID;
    };

    return (
        <Background>
            {!isMobile || (themeData?.themeNeed ?? true) ? getResponsiveLoginForm() : <SupportDesktopView />}
        </Background>
    );
};

export default LoginForm;